import type { PerfLab } from '../debug/perf/PerfLab';

export async function launchDiagnosticRun(
  perfLab: PerfLab,
  ctx: { trackId: string; landscape: string; raceMode: string; diagMode: 'full' | 'stress' },
): Promise<void> {
  const overlay = mountDiagOverlay();
  try {
    const { runDiagnostic } = await import('../diag/DiagnosticRunner');
    const twin = (window as unknown as { __twin: import('../diag/DiagnosticRunner').TwinDiagSurface }).__twin;
    const outcome = await runDiagnostic(perfLab, twin, {
      trackId: ctx.trackId,
      landscape: ctx.landscape,
      raceMode: ctx.raceMode,
      diagMode: ctx.diagMode,
      onStatus: (msg) => overlay.setStatus(msg),
    });
    overlay.setReport(outcome.upload.url, outcome.upload.storage, outcome.payload, outcome.upload.error);
  } catch (err) {
    console.error('[diag] run failed', err);
    overlay.setError(String(err instanceof Error ? err.message : err));
  }
}

interface DiagOverlayHandle {
  setStatus: (msg: string) => void;
  setReport: (
    url: string,
    storage: 'supabase' | 'local',
    payload: import('../diag/types').ReportPayload,
    uploadError?: string,
  ) => void;
  setError: (msg: string) => void;
}

function mountDiagOverlay(): DiagOverlayHandle {
  const root = document.createElement('div');
  root.style.cssText = `
    position: fixed; left: 50%; top: 12px; transform: translateX(-50%);
    z-index: 2147483647; max-width: min(640px, calc(100vw - 24px));
    padding: 12px 16px; border-radius: 6px;
    background: rgba(7, 11, 16, 0.92); color: #d8f7ff;
    border: 1px solid rgba(92, 193, 218, 0.65);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
    font: 12px/1.4 ui-monospace, Menlo, Consolas, monospace;
  `;
  const title = document.createElement('div');
  title.textContent = 'Diagnostic run';
  title.style.cssText = 'font-weight: 700; color: #ffffff; margin-bottom: 6px;';
  const status = document.createElement('div');
  status.textContent = 'starting...';
  status.style.cssText = 'opacity: 0.85; word-break: break-word;';
  const errorBox = document.createElement('div');
  errorBox.style.cssText = 'margin-top: 6px; color: #ffb3b3; word-break: break-word; font-size: 11px;';
  errorBox.hidden = true;
  const link = document.createElement('a');
  link.style.cssText = 'display: block; margin-top: 8px; color: #5cc1da; word-break: break-all;';
  link.target = '_blank';
  link.rel = 'noopener';
  link.hidden = true;
  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px;';
  buttonRow.hidden = true;
  const buttonStyle = `
    font: inherit; color: #d8f7ff;
    background: rgba(0, 182, 238, 0.18); border: 1px solid rgba(92, 193, 218, 0.65);
    border-radius: 3px; padding: 4px 10px; cursor: pointer;
  `;
  const makeButton = (label: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.type = 'button';
    btn.style.cssText = buttonStyle;
    return btn;
  };
  const copyUrlBtn = makeButton('Copy URL');
  const copyJsonBtn = makeButton('Copy JSON');
  const downloadBtn = makeButton('Download JSON');
  buttonRow.append(copyUrlBtn, copyJsonBtn, downloadBtn);
  root.append(title, status, errorBox, link, buttonRow);
  document.body.appendChild(root);

  let payloadRef: import('../diag/types').ReportPayload | null = null;

  const flashLabel = (btn: HTMLButtonElement, original: string, flash: string): void => {
    btn.textContent = flash;
    window.setTimeout(() => { btn.textContent = original; }, 1500);
  };

  copyUrlBtn.addEventListener('click', () => {
    const url = link.href;
    if (!url) return;
    void navigator.clipboard?.writeText(url).then(() => flashLabel(copyUrlBtn, 'Copy URL', 'Copied'));
  });
  copyJsonBtn.addEventListener('click', () => {
    if (!payloadRef) return;
    const json = JSON.stringify(payloadRef, null, 2);
    void navigator.clipboard?.writeText(json).then(
      () => flashLabel(copyJsonBtn, 'Copy JSON', 'Copied'),
      () => flashLabel(copyJsonBtn, 'Copy JSON', 'Copy failed'),
    );
  });
  downloadBtn.addEventListener('click', () => {
    if (!payloadRef) return;
    const json = JSON.stringify(payloadRef, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `tst-perf-${payloadRef.meta.id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after the click handler finishes; some browsers cancel the
    // download if the URL is revoked synchronously.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    flashLabel(downloadBtn, 'Download JSON', 'Saved');
  });

  return {
    setStatus: (msg) => { status.textContent = msg; },
    setReport: (url, storage, payload, uploadError) => {
      payloadRef = payload;
      const failedToCloud = storage === 'local' && Boolean(uploadError);
      if (failedToCloud) {
        status.textContent = 'Cloud upload failed — report URL only resolves on this device. Use Download/Copy JSON.';
        status.style.color = '#ffb3b3';
        errorBox.textContent = uploadError ?? '';
        errorBox.hidden = false;
      } else if (storage === 'supabase') {
        status.textContent = 'Report uploaded.';
      } else {
        status.textContent = 'Report saved locally (Supabase env not set).';
      }
      link.href = url;
      link.textContent = url;
      link.hidden = false;
      buttonRow.hidden = false;
      void navigator.clipboard?.writeText(url).catch(() => { /* no-op */ });
    },
    setError: (msg) => {
      status.textContent = `Failed: ${msg}`;
      status.style.color = '#ff8888';
    },
  };
}
